import BaseGraph, {
  resetGraphZoom,
} from 'features/Integrations/features/ConnectedDevices/features/DeviceGraph/components/DeviceBaseGraph';
import { ReactNode, RefObject, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import DeviceGraphDataSheet from '../components/DeviceGraphDataSheet';
import { GraphBuildResult, GraphControl } from '../types/GraphTypes';

type GraphRendererProps = {
  result: GraphBuildResult;
  chartRef: RefObject<any>;
  startTime?: number;
  endTime?: number;
  onViewDevice?: () => void;
};

const renderControl = (control: GraphControl) => (
  <Button
    key={control.key}
    type="button"
    size="sm"
    className={control.className ?? 'm-1 px-2 py-1'}
    onClick={control.onClick}
    aria-label={control.ariaLabel}
    title={control.title}
  >
    {control.icon}
    {control.label}
  </Button>
);

const renderEmptyState = (
  title: ReactNode,
  message: ReactNode,
  detail: ReactNode,
  action?: GraphControl,
) => (
  <div className="bg-primary h-100 rounded-3 overflow-hidden d-flex flex-column justify-content-center align-items-center text-white text-center px-4">
    {title ? <div className="fs-4 fw-semibold mb-2">{title}</div> : null}
    {message ? <div className="text-theme-muted mb-2">{message}</div> : null}
    {detail ? <div className="small text-theme-muted mb-3">{detail}</div> : null}
    {action ? renderControl(action) : null}
  </div>
);

export default function GraphRenderer({
  result,
  chartRef,
  startTime,
  endTime,
  onViewDevice,
}: GraphRendererProps) {
  const onEvents = useMemo(
    () => (result.onChartClick ? { click: result.onChartClick } : undefined),
    [result.onChartClick],
  );

  if (result.loading) {
    return <BaseGraph loading loadingMessage={result.loadingMessage ?? 'Loading device data...'} />;
  }

  if (!result.option) {
    if (result.fallbackDatasheetDevice) {
      return (
        <DeviceGraphDataSheet
          device={result.fallbackDatasheetDevice}
          startTime={startTime}
          endTime={endTime}
          onViewDevice={onViewDevice}
        />
      );
    }

    if (result.emptyState) {
      return renderEmptyState(
        result.emptyState.title,
        result.emptyState.message,
        result.emptyState.detail,
        result.emptyState.action,
      );
    }

    return <BaseGraph emptyMessage={result.emptyMessage ?? 'No device selected'} />;
  }

  return (
    <BaseGraph
      chartRef={chartRef}
      option={result.option}
      onEvents={onEvents}
      onChartReady={result.onChartReady}
      controlsLeft={(result.controlsLeft ?? []).map(renderControl)}
      controlsRight={
        <div className="d-flex align-items-center gap-1">
          {(result.controlsRight ?? []).map(renderControl)}
          {renderControl({
            key: 'reset-zoom',
            label: 'Reset Zoom',
            onClick: () => resetGraphZoom(chartRef),
          })}
        </div>
      }
    />
  );
}
